import { useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import {
  Bell,
  BellOff,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2
} from "lucide-react";
import { cn } from "../lib/utils";

// API imports
const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Status configuration
const REMINDER_STATUS_CONFIG = {
  pending: {
    label: "Ausstehend",
    color: "gray",
    badgeClass: "bg-gray-100 text-gray-700 border-gray-200",
    icon: Clock,
    description: "Erinnerung noch nicht gesendet"
  },
  sent: {
    label: "Gesendet",
    color: "blue",
    badgeClass: "bg-blue-100 text-blue-700 border-blue-200",
    icon: Send,
    description: "An Resend übergeben"
  },
  delivered: {
    label: "Zugestellt",
    color: "green",
    badgeClass: "bg-green-100 text-green-700 border-green-200",
    icon: CheckCircle,
    description: "E-Mail wurde zugestellt"
  },
  bounced: {
    label: "Nicht zustellbar",
    color: "red",
    badgeClass: "bg-red-100 text-red-700 border-red-200",
    icon: XCircle,
    description: "E-Mail konnte nicht zugestellt werden"
  },
  failed: {
    label: "Fehlgeschlagen",
    color: "red",
    badgeClass: "bg-red-100 text-red-700 border-red-200",
    icon: AlertTriangle,
    description: "Versand fehlgeschlagen"
  }
};

// Format date helper
const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  try {
    const date = new Date(isoString);
    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return isoString;
  }
};

/**
 * Reminder Status Badge - Zeigt den Erinnerungsstatus an
 */
export function ReminderStatusBadge({ status, sentAt, size = "default" }) {
  const config = REMINDER_STATUS_CONFIG[status] || REMINDER_STATUS_CONFIG.pending;
  const Icon = config.icon;
  
  const sizeClasses = size === "small" 
    ? "text-xs px-1.5 py-0.5" 
    : "text-sm px-2 py-1";

  // Wenn keine Erinnerung gesendet wurde und kein Status
  if (!status && !sentAt) {
    return (
      <Badge variant="outline" className={cn("bg-gray-50 text-gray-500 border-gray-200", sizeClasses)}>
        <BellOff className={size === "small" ? "h-3 w-3 mr-1" : "h-4 w-4 mr-1"} />
        Keine Erinnerung
      </Badge>
    );
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={cn(config.badgeClass, sizeClasses, "inline-flex items-center gap-1")}
          >
            <Icon className={size === "small" ? "h-3 w-3" : "h-4 w-4"} />
            {config.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{config.description}</p>
          {sentAt && (
            <p className="text-xs text-muted-foreground mt-1">
              Gesendet: {formatDateTime(sentAt)}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Reminder Controls - Steuerungselemente für Terminerinnerungen
 */
export function ReminderControls({
  appointment,
  customerId,
  onUpdate,
  showToggle = true,
  showSendButton = true
}) {
  const [loading, setLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  
  const reminderEnabled = appointment?.appointment_reminder_enabled !== false;
  const reminderSentAt = appointment?.appointment_reminder_sent_at;
  const reminderStatus = appointment?.appointment_reminder_status;
  
  // Prüfe ob Termin in der Zukunft liegt
  const isFutureAppointment = () => {
    if (!appointment?.date) return false;
    const appointmentDate = new Date(appointment.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return appointmentDate >= today;
  };
  
  // Toggle Erinnerung aktivieren/deaktivieren
  const handleToggleReminder = async () => {
    if (!appointment?.id || !customerId) return;
    
    setToggleLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/customers/${customerId}/appointments/${appointment.id}/reminder-settings?enabled=${!reminderEnabled}`,
        {
          method: "PUT",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Fehler beim Aktualisieren");
      }
      
      toast.success(reminderEnabled 
        ? "Erinnerung deaktiviert" 
        : "Erinnerung aktiviert"
      );
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Failed to toggle reminder:", error);
      toast.error(error.message || "Fehler beim Aktualisieren");
    } finally {
      setToggleLoading(false);
    }
  };
  
  // Erinnerung jetzt senden
  const handleSendReminder = async () => {
    if (!appointment?.id || !customerId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/customers/${customerId}/appointments/${appointment.id}/send-reminder`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Fehler beim Senden");
      }
      
      const result = await response.json();
      toast.success(result.message || "Erinnerung gesendet");
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Failed to send reminder:", error);
      toast.error(error.message || "Fehler beim Senden der Erinnerung");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-3">
      {/* Status Badge */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Erinnerung:</span>
        <ReminderStatusBadge status={reminderStatus} sentAt={reminderSentAt} />
      </div>
      
      {/* Controls */}
      <div className="flex items-center gap-4">
        {/* Toggle */}
        {showToggle && (
          <div className="flex items-center gap-2">
            <Switch
              id="reminder-toggle"
              checked={reminderEnabled}
              onCheckedChange={handleToggleReminder}
              disabled={toggleLoading || !!reminderSentAt}
            />
            <Label 
              htmlFor="reminder-toggle" 
              className={cn(
                "text-sm cursor-pointer",
                !reminderEnabled && "text-muted-foreground"
              )}
            >
              {toggleLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                reminderEnabled ? "Aktiv" : "Inaktiv"
              )}
            </Label>
          </div>
        )}
        
        {/* Send Button */}
        {showSendButton && isFutureAppointment() && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={loading || !reminderEnabled}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Bell className="h-4 w-4 mr-1" />
                )}
                {reminderSentAt ? "Erneut senden" : "Jetzt senden"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Terminerinnerung senden?</AlertDialogTitle>
                <AlertDialogDescription>
                  {reminderSentAt ? (
                    <>
                      Es wurde bereits eine Erinnerung am {formatDateTime(reminderSentAt)} gesendet.
                      Möchten Sie eine weitere Erinnerung senden?
                    </>
                  ) : (
                    <>
                      Möchten Sie jetzt eine Terminerinnerung an{" "}
                      <strong>{appointment?.customer_email}</strong> senden?
                    </>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={handleSendReminder}>
                  Senden
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      
      {/* Info wenn Termin in Vergangenheit */}
      {!isFutureAppointment() && (
        <p className="text-xs text-muted-foreground">
          Erinnerungen können nur für zukünftige Termine gesendet werden.
        </p>
      )}
      
      {/* Fehler-Hinweis bei Bounce/Failed */}
      {(reminderStatus === "bounced" || reminderStatus === "failed") && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 inline mr-1" />
          Die Erinnerung konnte nicht zugestellt werden. Bitte prüfen Sie die E-Mail-Adresse.
        </div>
      )}
    </div>
  );
}

/**
 * Appointment Card with Reminder Info - Termin-Karte mit Erinnerungsinformationen
 */
export function AppointmentReminderCard({ appointment, customerId, onUpdate }) {
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      const weekdays = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
      return `${weekdays[date.getDay()]}, ${date.toLocaleDateString("de-DE")}`;
    } catch {
      return dateStr;
    }
  };
  
  return (
    <div className="border rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-medium">
            Termin: {formatDate(appointment?.date)} {appointment?.time && `um ${appointment.time} Uhr`}
          </h4>
          <p className="text-sm text-muted-foreground">
            {appointment?.customer_name}
            {appointment?.customer_email && ` • ${appointment.customer_email}`}
          </p>
        </div>
        
        {/* Status Badge */}
        <ReminderStatusBadge 
          status={appointment?.appointment_reminder_status}
          sentAt={appointment?.appointment_reminder_sent_at}
        />
      </div>
      
      {/* Location */}
      {(appointment?.customer_address || appointment?.location_note) && (
        <p className="text-sm">
          <span className="text-muted-foreground">Ort:</span>{" "}
          {appointment.customer_address}
          {appointment.location_note && <em className="ml-1">({appointment.location_note})</em>}
        </p>
      )}
      
      {/* Reminder Controls */}
      <ReminderControls
        appointment={appointment}
        customerId={customerId}
        onUpdate={onUpdate}
      />
    </div>
  );
}

export default {
  ReminderStatusBadge,
  ReminderControls,
  AppointmentReminderCard
};
